import { ResponsiveLine } from '@nivo/line';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatInteger } from '../../../common/formatHelper';
import { SkuSnapshot } from '../../types/metrics';

interface SkuLevelSummaryProps {
  data: SkuSnapshot[];
}

const SparkLine: React.FC<{ data: number[] }> = ({ data }) => {
  const chartData = [
    {
      id: 'trend',
      data: data.map((value, index) => ({
        x: index,
        y: value,
      })),
    },
  ];

  return (
    <div className="h-[50px] w-[120px]">
      <ResponsiveLine
        data={chartData}
        margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
          stacked: false,
        }}
        curve="monotoneX"
        enableArea={true}
        areaOpacity={0.15}
        enablePoints={false}
        enableGridX={false}
        enableGridY={false}
        axisTop={null}
        axisRight={null}
        axisBottom={null}
        axisLeft={null}
        colors={['#4299e1']}
        theme={{
          background: 'transparent',
        }}
        animate={false}
      />
    </div>
  );
};

const SkuLevelSummary: React.FC<SkuLevelSummaryProps> = ({ data }) => {
  const { t } = useTranslation('app');

  return (
    <div className="bg-app-panel-dark rounded-lg p-4">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">
          {t('SKU Level Summary')}
        </h3>
        <div className="flex gap-2">
          <button className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700">
            {t('Top 25')}
          </button>
          <button className="rounded bg-gray-600 px-3 py-1 text-sm text-white hover:bg-gray-700">
            {t('All SKUs')}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full table-auto">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-300">
                {t('SKU')}
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-300">
                {t('Stock Available')}
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-300">
                {t('Units Shipped')}
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-300">
                {t('Units Pending')}
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold text-gray-300">
                {t('Trending')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((sku, index) => (
              <tr
                key={sku.groupKey}
                className={`border-b border-gray-700 ${
                  index % 2 === 0 ? 'bg-gray-800/30' : ''
                }`}
              >
                <td className="px-4 py-2 text-sm text-white">{sku.groupKey}</td>
                <td className="px-4 py-2 text-sm text-white">
                  {formatInteger(sku.stockAvailable)}
                </td>
                <td className="px-4 py-2 text-sm text-white">
                  {formatInteger(sku.unitsShipped)}
                </td>
                <td className="px-4 py-2 text-sm text-white">
                  {formatInteger(sku.unitsPending)}
                </td>
                <td className="px-4 py-2">
                  <SparkLine data={sku.trend} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SkuLevelSummary;
